import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import LinearProgress from '@material-ui/core/LinearProgress';

function EnhancedTableHead(props) {
    const { classes, order, orderBy, onRequestSort, headCells } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        {headCell.sortable === false ? (
                            headCell.label
                        ) : (
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <span className={classes.visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </span>
                                ) : null}
                            </TableSortLabel>
                        )}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    headCells: PropTypes.array.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        gap: theme.spacing(2),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    title: {
        flex: '1 1 100%',
    },
}));

const EnhancedTableToolbar = ({ title, children }) => {
    const classes = useToolbarStyles();

    return (
        <Toolbar className={clsx(classes.root, classes.highlight)}>
            <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                {title}
            </Typography>
            {children}
        </Toolbar>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 750,
    },
    clickable: {
        cursor: 'pointer',
    },
    progress: {
        height: 3,
    },
    progressPlaceholder: {
        height: 3,
    },
    empty: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
}));

/**
 * Server-paginated, sortable table. The caller owns order/orderBy/page/rowsPerPage
 * and fetches rows itself.
 *
 * headCells: [{ id, label, numeric?, disablePadding?, sortable?, render?(value, row, handleAction) }]
 * Optional: onRowClick(row), selectedId + rowKey for highlighting, title / toolbarContent,
 * emptyMessage, isLoading (thin progress bar; the table stays mounted), rowsPerPageOptions.
 */
export default function PaginatedTable({
    rows,
    rowCount,
    order,
    orderBy,
    setOrder,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    headCells,
    handleAction,
    onRowClick,
    selectedId,
    rowKey = 'id',
    title,
    toolbarContent,
    emptyMessage,
    isLoading = false,
    rowsPerPageOptions = [5, 10, 25],
}) {
    const classes = useStyles();

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc', property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
    };

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <EnhancedTableToolbar title={title}>{toolbarContent}</EnhancedTableToolbar>
                {isLoading ? (
                    <LinearProgress className={classes.progress} />
                ) : (
                    <div className={classes.progressPlaceholder} />
                )}
                <TableContainer>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            classes={classes}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                        />
                        <TableBody>
                            {rows.length === 0 && emptyMessage && !isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={headCells.length} className={classes.empty}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {rows.map((row, index) => {
                                const key = row[rowKey] !== undefined ? `row-${row[rowKey]}-${index}` : `row-${index}`;
                                const selected = selectedId !== undefined && selectedId !== null && row[rowKey] === selectedId;
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={key}
                                        selected={selected}
                                        className={onRowClick ? classes.clickable : undefined}
                                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                                    >
                                        {headCells.map((cell, id) => {
                                            return (
                                                <TableCell align={cell.numeric ? 'right' : 'left'} key={`tablecol-row-key-${id}`}>
                                                    {cell.render
                                                        ? cell.render(row[cell.id], row, handleAction)
                                                        : row[cell.id]}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={rowCount || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>
        </div>
    );
}
